import { useTaskStore } from '@/lib/task-store';
import { useOwnerStore } from '@/lib/owner-store';
import { useState, useEffect } from 'react';
import { StatusBadge } from '../tasks/status-badge';
import { Building2, Mail, User, Calendar, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

interface ScrumRosterProps {
  hideEmptyMembers: boolean;
}

export function ScrumRoster({ hideEmptyMembers }: ScrumRosterProps) {
  const { owners, fetchOwners } = useOwnerStore();
  const { tasks, fetchTasks } = useTaskStore();
  const [expandedOwners, setExpandedOwners] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchOwners();
    fetchTasks();
  }, [fetchOwners, fetchTasks]);

  const getOwnerTasks = (ownerId: string) => {
    return tasks.filter(task => 
      task.owner_id === ownerId && 
      task.status !== 'Closed'
    );
  };

  const toggleOwner = (ownerId: string) => {
    setExpandedOwners(prev => ({
      ...prev,
      [ownerId]: !prev[ownerId]
    }));
  };

  const filteredOwners = hideEmptyMembers 
    ? owners.filter(owner => getOwnerTasks(owner.id).length > 0)
    : owners;

  return (
    <div className="space-y-6">      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredOwners.map(owner => {
          const ownerTasks = getOwnerTasks(owner.id);
          const highPriorityTasks = ownerTasks.filter(task => task.priority === 'High');
          const isExpanded = expandedOwners[owner.id];
          
          return (
            <div 
              key={owner.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate">{owner.name}</h4>
                    {owner.email && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{owner.email}</span>
                      </div>
                    )}
                    {owner.organization && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Building2 className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{owner.organization}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-4">
                <button
                  onClick={() => toggleOwner(owner.id)}
                  className="w-full flex items-center justify-between text-left mb-3 group"
                >
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Active Tasks
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {ownerTasks.length} total
                      </span>
                      {highPriorityTasks.length > 0 && (
                        <span className="flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                          <AlertCircle className="w-3 h-3" />
                          {highPriorityTasks.length} high priority
                        </span>
                      )}
                    </div>
                    <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-gray-600 transition-colors">
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                    {ownerTasks.length > 0 ? (
                      <div className="space-y-2">
                        {ownerTasks.map(task => (
                          <div 
                            key={task.id}
                            className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium truncate">{task.title}</span>
                              <StatusBadge status={task.status} />
                            </div>
                            {task.due_date && (
                              <div className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                <Calendar className="w-3 h-3" />
                                <span>Due {new Date(task.due_date).toLocaleDateString()}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                        No active tasks
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}